import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { useGerminationChambers } from "../../hooks/useSowingInstructions";
import { useAmendSowing } from "../../hooks/useSowings";
import { useNotification } from "@/shared/hooks/useNotification";
import { useConfirm } from "@/shared/contexts/confirm-context";
import type { Sowing } from "../../services/sowingsApi";
import { SowingContextBlock } from "./SowingContextBlock";

interface AmendSowingDialogProps {
  sowing: Sowing;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

const NO_CHAMBER = "__none__";

const toDateInput = (iso: string) => iso.slice(0, 10);

export const AmendSowingDialog = ({
  sowing,
  open,
  onOpenChange,
  onSuccess,
}: AmendSowingDialogProps) => {
  const [traysSown, setTraysSown] = useState(String(sowing.traysSown));
  const [sowingDate, setSowingDate] = useState(toDateInput(sowing.sowingDate));
  const [chamberId, setChamberId] = useState(
    sowing.germinationChamberId ?? NO_CHAMBER
  );
  const [notes, setNotes] = useState(sowing.notes ?? "");
  const [reason, setReason] = useState("");

  // Re-seed local state when reopened against a different row.
  useEffect(() => {
    if (open) {
      setTraysSown(String(sowing.traysSown));
      setSowingDate(toDateInput(sowing.sowingDate));
      setChamberId(sowing.germinationChamberId ?? NO_CHAMBER);
      setNotes(sowing.notes ?? "");
      setReason("");
    }
  }, [open, sowing]);

  const chambersQuery = useGerminationChambers();
  const amend = useAmendSowing();
  const confirm = useConfirm();
  const notify = useNotification();

  const traysChanged = parseFloat(traysSown) !== sowing.traysSown;
  const reasonRequired = traysChanged;

  const handleSubmit = () => {
    const trays = parseFloat(traysSown);
    if (!Number.isFinite(trays) || trays <= 0) {
      notify.error("TraysSown must be greater than zero.");
      return;
    }
    if (reasonRequired && !reason.trim()) {
      notify.error("Reason is required when changing TraysSown.");
      return;
    }

    const payload = {
      traysSown: trays !== sowing.traysSown ? trays : undefined,
      sowingDate:
        sowingDate !== toDateInput(sowing.sowingDate)
          ? new Date(`${sowingDate}T00:00:00Z`).toISOString()
          : undefined,
      germinationChamberId:
        chamberId === NO_CHAMBER
          ? sowing.germinationChamberId
            ? null
            : undefined
          : chamberId !== sowing.germinationChamberId
          ? chamberId
          : undefined,
      notes: notes !== (sowing.notes ?? "") ? notes : undefined,
      reason: reason.trim() || undefined,
    };

    const anyChange =
      payload.traysSown !== undefined ||
      payload.sowingDate !== undefined ||
      payload.germinationChamberId !== undefined ||
      payload.notes !== undefined;

    if (!anyChange) {
      notify.info("No changes to save.");
      return;
    }

    confirm({
      title: "Amend sowing?",
      description: "The recorded sowing details will be updated.",
      confirmLabel: "Save changes",
      onConfirm: () =>
        amend
          .mutateAsync({ sowingId: sowing.id, payload })
          .then(() => {
            notify.success("Sowing amended.");
            onSuccess?.();
            onOpenChange(false);
          })
          .catch((error: any) => {
            notify.error(
              error.response?.data?.message || "Failed to amend sowing."
            );
            throw error;
          }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Amend Sowing</DialogTitle>
          <DialogDescription>
            Edit fields on this sowing record. Allowed only while no germination
            has been recorded.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <SowingContextBlock sowing={sowing} />

          <Field>
            <FieldLabel>Trays sown</FieldLabel>
            <Input
              type="number"
              min={0}
              step="0.01"
              value={traysSown}
              onChange={(e) => setTraysSown(e.target.value)}
              disabled={amend.isPending}
            />
          </Field>

          <Field>
            <FieldLabel>Sowing date</FieldLabel>
            <DatePicker
              value={sowingDate}
              onChange={setSowingDate}
              disabled={amend.isPending}
            />
          </Field>

          <Field>
            <FieldLabel>Germination chamber</FieldLabel>
            <Select
              value={chamberId}
              onValueChange={setChamberId}
              disabled={amend.isPending || chambersQuery.isLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select chamber" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_CHAMBER}>(none)</SelectItem>
                {(chambersQuery.data ?? []).map((c: any) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name ?? c.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field>
            <FieldLabel>Notes</FieldLabel>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={amend.isPending}
              maxLength={1000}
            />
          </Field>

          <Field>
            <FieldLabel>
              Reason{" "}
              {reasonRequired && <span className="text-red-500">*</span>}
            </FieldLabel>
            <Textarea
              placeholder={
                reasonRequired
                  ? "Required when changing trays sown"
                  : "Optional"
              }
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={amend.isPending}
              maxLength={1000}
            />
          </Field>

          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => onOpenChange(false)}
              disabled={amend.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                amend.isPending || (reasonRequired && !reason.trim())
              }
            >
              {amend.isPending ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
